import { HStack, Text } from '@chakra-ui/react';
import { errorToastOptions, toaster } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import useCurrentDateTime from '@hooks/useCurrentDateTime';
import { usePrevious } from '@hooks/usePrevious';
import { selectSituationTime, selectSituationVersion } from '@redux/situation/situation.selectors';
import { timeDuration } from '@utils/date.utils';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

export default function CockpitTime() {
  const { formatMessage } = useIntl();
  const currentSituationTime = useAppSelector(selectSituationTime);
  const situationVersion = useAppSelector(selectSituationVersion);
  const previousSituationTime = usePrevious(currentSituationTime);
  const previousSituationVersion = usePrevious(situationVersion);
  const [isBroken, setIsBroken] = useState<boolean>(currentSituationTime == null);
  const localeDate = useCurrentDateTime();

  //If there's more than 5s between the two last situation, system is considered "broken"
  useEffect(() => {
    if (previousSituationTime && currentSituationTime && previousSituationTime !== currentSituationTime) {
      if (previousSituationVersion !== -1) {
        if (timeDuration(previousSituationTime, currentSituationTime) > 5000) {
          if (!isBroken) {
            setIsBroken(true);
            if (!toaster.isVisible('noServerResponse')) {
              toaster.create({
                ...errorToastOptions,
                title: formatMessage({ id: 'components.cockpit.noServerResponse' }),
                id: 'noServerResponse',
              });
            }
          }
        } else if (isBroken) {
          setIsBroken(false);
        }
      }
    }
  }, [
    currentSituationTime,
    formatMessage,
    isBroken,
    previousSituationTime,
    previousSituationVersion,
    situationVersion,
  ]);

  //If we did not receive a situation for more than 5s, system is considered "broken"
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsBroken(true);
      if (!toaster.isVisible('noServerResponse')) {
        toaster.create({
          ...errorToastOptions,
          title: formatMessage({ id: 'components.cockpit.noServerResponse' }),
          id: 'noServerResponse',
        });
      }
    }, 5000);
    return () => {
      clearTimeout(timeout);
    };
  }, [currentSituationTime, formatMessage]);

  const date = isBroken || !currentSituationTime ? new Date(localeDate) : new Date(currentSituationTime);

  return (
    <Tooltip
      disabled={!isBroken}
      positioning={{
        placement: 'top-start',
      }}
      content={formatMessage({ id: 'components.cockpit.degraded' })}
    >
      <HStack
        gap={3}
        justifyContent="center"
        height="100%"
        paddingX={0.5}
        paddingY={0.25}
        marginLeft={0.25}
        marginRight="standardXS"
        cursor="default"
        border="2px solid"
        backgroundColor={isBroken ? 'red.800' : 'transparent'}
        borderColor={isBroken ? 'red.500' : 'transparent'}
      >
        <HStack gap={0}>
          <Text textStyle="screenMed" color={isBroken ? 'neutral.white' : 'neutral.200'}>
            {format(date, 'HH:mm')}
          </Text>
          <Text textStyle="screenMed" color={isBroken ? 'neutral.white' : 'neutral.300'}>
            {format(date, ':ss')}
          </Text>
        </HStack>
        <Text textStyle="screenMed" color={isBroken ? 'neutral.white' : 'neutral.300'}>
          {format(date, 'dd/MM/yyyy')}
        </Text>
      </HStack>
    </Tooltip>
  );
}
